// zoom-fader.ts
import { Container } from "pixi.js";
import type { Viewport } from "pixi-viewport";

export type ZoomFaderOpts = {
  /** World tile size at scale=1 */
  tilePx: number;
  /** Start fading in when pixels-per-tile >= this (default: 10) */
  fadeStartPx?: number;
  /** Fully visible when pixels-per-tile >= this (default: 18) */
  fadeEndPx?: number;
  /** Minimal alpha while fading (default: 0) */
  minAlpha?: number;
  /** Max alpha when fully visible (default: 1) */
  maxAlpha?: number;
  /** If true, sets renderable=false when alpha==0 to skip draw (default: true) */
  disableWhenHidden?: boolean;
};

/**
 * Attach a zoom-based fade to one or more display objects.
 * Numbers become visible as tiles get larger on screen.
 */
export function attachZoomFader(
  viewport: Viewport,
  target: Container,
  {
    tilePx,
    fadeStartPx = 10,
    fadeEndPx = 18,
    minAlpha = 0,
    maxAlpha = 1,
    disableWhenHidden = true,
  }: ZoomFaderOpts
): () => void {
  const objs = [target];

  const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
  const smoothstep = (a: number, b: number, x: number) => {
    const t = clamp01((x - a) / (b - a));
    return t * t * (3 - 2 * t);
  };

  const update = () => {
    // Pixels-per-tile on screen (use the stricter/min to handle non-uniform scales)
    const sX = viewport.scale.x;
    const sY = viewport.scale.y;
    const pxPerTile = tilePx * Math.min(sX, sY);

    // Smooth fade from fadeStartPx → fadeEndPx
    const t = smoothstep(fadeStartPx, fadeEndPx, pxPerTile);
    const alpha = minAlpha + (maxAlpha - minAlpha) * t;

    for (const obj of objs) {
      obj.alpha = alpha;
      if (disableWhenHidden) obj.renderable = alpha > 0.001;
    }
  };

  // Hook viewport events that change scale
  viewport.on("zoomed", update);
  // Also run once now (before any interaction)
  update();

  // Return an unsubscribe/cleanup
  return () => {
    viewport.off("zoomed", update);
  };
}
